// ts-rest contract for the Contacts tier (three-tier client model Stage 1,
// ADR-0025).
//
// One area covering BOTH Object Types behind the one directory surface: the
// person `Contact` (book-owner-scoped) and the firm-shared
// `OrganisationContact` (ADR-0025 D0.4). This is a per-view presentation
// projection, NOT a domain model; the controller maps `ctx.read(...)` onto
// these DTOs and dispatches mutations through the Contact Actions.
//
// Endpoints under /api/contacts and /api/organisation-contacts:
//   GET   /api/contacts                        — unified directory (both types)
//   GET   /api/contacts/duplicate-candidates   — advisory duplicate check
//   GET   /api/contacts/:contactId             — detail (person or organisation)
//   POST  /api/contacts                        — create person (create-contact)
//   PATCH /api/contacts/:contactId             — edit person (update-contact)
//   POST  /api/contacts/:contactId/status      — lifecycle (activate/close/reopen)
//   POST  /api/organisation-contacts                     — create organisation
//   PATCH /api/organisation-contacts/:contactId          — edit organisation
//   POST  /api/organisation-contacts/:contactId/status   — lifecycle

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { errorBodyWithCode as errorBody } from '../schemas/common';

const c = initContract();

// ---------------------------------------------------------------------------
// Enums (closed domain values — snake_case VALUES, camelCase keys)
// ---------------------------------------------------------------------------

export const contactStatusSchema = z.enum(['draft', 'active', 'closed']);
export type ContactStatus = z.infer<typeof contactStatusSchema>;

/** Presentation discriminator across the two Object Types. */
export const contactKindSchema = z.enum(['person', 'organisation']);
export type ContactKind = z.infer<typeof contactKindSchema>;

export const organisationTypeSchema = z.enum(['sipp_provider', 'accountant', 'solicitor', 'other']);
export type OrganisationType = z.infer<typeof organisationTypeSchema>;

export const contactLifecycleActionSchema = z.enum(['activate', 'close', 'reopen']);
export type ContactLifecycleAction = z.infer<typeof contactLifecycleActionSchema>;

// ---------------------------------------------------------------------------
// Directory rows + stats
// ---------------------------------------------------------------------------

export const contactRowSchema = z.object({
    contactId: z.string(),
    contactKind: contactKindSchema,
    displayName: z.string(),
    /** The surname alone — the directory's FIRST column and its default A-Z
     * sort (SEP-174). Projected by `projectContactName`, so it prefers the
     * contact's structured `lastName` and falls back to a derivation from the
     * display name. Null for organisations, which have no surname. */
    lastName: z.string().nullable(),
    status: contactStatusSchema,
    /** Organisation rows only; null for persons. */
    organisationType: organisationTypeSchema.nullable(),
    /** Person rows only; null for organisations (firm-shared, no owner). */
    ownerImUserId: z.string().nullable(),
    /** Resolved IM display name, or null. */
    ownerImName: z.string().nullable(),
    email: z.string().nullable(),
    /** The contact's MOBILE number. `Contact.phone` is the mobile column in the
     * CRM export — there is no separate `mobilePhone` field anywhere in the
     * ontology or the table, and the directory labels this "Mobile". */
    phone: z.string().nullable(),
    /** `Contact.homePhone` — the landline, shown next to Mobile. */
    homePhone: z.string().nullable(),
    city: z.string().nullable(),
    /** No longer a directory column (dropped in SEP-174); still on the wire for
     * callers that sort or export on it. */
    inceptionDate: z.string().nullable(),
    /** False for every row until the Suitability Record tier links (Stage 4);
     * backs the "No Suitability Record" filter chip. */
    hasSuitabilityRecord: z.boolean(),
});
export type ContactRow = z.infer<typeof contactRowSchema>;

/**
 * The fields a directory search looks at, in one place so the server-side
 * filter (`?search=`, used by the relationship picker) and the client-side
 * filter (the directory, which reads the full row set) can never disagree about
 * which rows a query matches. Feed it to `matchesAllTokens` — see
 * `token-search.ts` for why the match is token-AND rather than `includes`.
 */
export function contactRowSearchFields(row: ContactRow): ReadonlyArray<string | null> {
    return [
        row.displayName,
        row.lastName,
        row.email,
        row.phone,
        row.homePhone,
        row.city,
        row.ownerImName,
    ];
}

export const contactsStatsSchema = z.object({
    total: z.number().int(),
    people: z.number().int(),
    organisations: z.number().int(),
    drafts: z.number().int(),
});
export type ContactsStats = z.infer<typeof contactsStatsSchema>;

export const listContactsResponseSchema = z.object({
    rows: z.array(contactRowSchema),
    stats: contactsStatsSchema,
});
export type ListContactsResponse = z.infer<typeof listContactsResponseSchema>;

// ---------------------------------------------------------------------------
// Detail — discriminated on contactKind
// ---------------------------------------------------------------------------

const provenanceFields = {
    sourceSystem: z.string().nullable(),
    crmContactId: z.string().nullable(),
    lastManualEditAt: z.string().nullable(),
    lastIngestAt: z.string().nullable(),
    /** Per-field provenance (ADR-0025 D8a) — human-touched field names. */
    manuallyEditedFields: z.array(z.string()),
};

export const personContactDetailSchema = z.object({
    contactKind: z.literal('person'),
    contactId: z.string(),
    displayName: z.string(),
    status: contactStatusSchema,
    inceptionDate: z.string().nullable(),
    ownerImUserId: z.string(),
    ownerImName: z.string().nullable(),
    firstName: z.string().nullable(),
    middleNames: z.string().nullable(),
    lastName: z.string().nullable(),
    title: z.string().nullable(),
    dateOfBirth: z.string().nullable(),
    gender: z.string().nullable(),
    maritalStatus: z.string().nullable(),
    nationality: z.string().nullable(),
    countryOfResidence: z.string().nullable(),
    addressLine1: z.string().nullable(),
    addressLine2: z.string().nullable(),
    city: z.string().nullable(),
    county: z.string().nullable(),
    postcode: z.string().nullable(),
    country: z.string().nullable(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    secondaryEmail: z.string().nullable(),
    homePhone: z.string().nullable(),
    workNumber: z.string().nullable(),
    employer: z.string().nullable(),
    occupation: z.string().nullable(),
    preferredMethodOfContact: z.string().nullable(),
    vulnerable: z.boolean().nullable(),
    vulnerableNotes: z.string().nullable(),
    hasSuitabilityRecord: z.boolean(),
    ...provenanceFields,
});
export type PersonContactDetail = z.infer<typeof personContactDetailSchema>;

export const organisationContactDetailSchema = z.object({
    contactKind: z.literal('organisation'),
    contactId: z.string(),
    displayName: z.string(),
    status: contactStatusSchema,
    inceptionDate: z.string().nullable(),
    organisationName: z.string(),
    organisationType: organisationTypeSchema,
    organisationTypeOther: z.string().nullable(),
    namedIndividual: z.string().nullable(),
    registeredAddressLine1: z.string().nullable(),
    registeredAddressLine2: z.string().nullable(),
    registeredCity: z.string().nullable(),
    registeredCounty: z.string().nullable(),
    registeredPostcode: z.string().nullable(),
    registeredCountry: z.string().nullable(),
    correspondenceAddressLine1: z.string().nullable(),
    correspondenceAddressLine2: z.string().nullable(),
    correspondenceCity: z.string().nullable(),
    correspondenceCounty: z.string().nullable(),
    correspondencePostcode: z.string().nullable(),
    correspondenceCountry: z.string().nullable(),
    hasSuitabilityRecord: z.boolean(),
    ...provenanceFields,
});
export type OrganisationContactDetail = z.infer<typeof organisationContactDetailSchema>;

export const contactDetailSchema = z.discriminatedUnion('contactKind', [
    personContactDetailSchema,
    organisationContactDetailSchema,
]);
export type ContactDetail = z.infer<typeof contactDetailSchema>;

// ---------------------------------------------------------------------------
// Duplicate candidates (advisory, never blocking — business answer #10)
// ---------------------------------------------------------------------------

export const contactDuplicateCandidateSchema = z.object({
    contactId: z.string(),
    displayName: z.string(),
    reasons: z.array(z.string()),
});
export type ContactDuplicateCandidateDto = z.infer<typeof contactDuplicateCandidateSchema>;

export const duplicateCandidatesResponseSchema = z.object({
    candidates: z.array(contactDuplicateCandidateSchema),
});
export type DuplicateCandidatesResponse = z.infer<typeof duplicateCandidatesResponseSchema>;

// ---------------------------------------------------------------------------
// Request bodies
// ---------------------------------------------------------------------------

const personEditableFields = {
    firstName: z.string().optional(),
    middleNames: z.string().optional(),
    lastName: z.string().optional(),
    title: z.string().optional(),
    dateOfBirth: z.string().optional(),
    gender: z.string().optional(),
    maritalStatus: z.string().optional(),
    nationality: z.string().optional(),
    countryOfResidence: z.string().optional(),
    addressLine1: z.string().optional(),
    addressLine2: z.string().optional(),
    city: z.string().optional(),
    county: z.string().optional(),
    postcode: z.string().optional(),
    country: z.string().optional(),
    email: z.string().optional(),
    phone: z.string().optional(),
    secondaryEmail: z.string().optional(),
    homePhone: z.string().optional(),
    workNumber: z.string().optional(),
    employer: z.string().optional(),
    occupation: z.string().optional(),
    preferredMethodOfContact: z.string().optional(),
    vulnerable: z.boolean().optional(),
    vulnerableNotes: z.string().optional(),
    inceptionDate: z.string().optional(),
};

export const createContactRequestSchema = z.object({
    /**
     * The owning IM — ONLY honoured for an Ops caller (an explicit UI pick,
     * validated server-side against the active desk roster). For a Desk
     * caller the server resolves the owner from the actor's own book and this
     * field is ignored (ADR-0025 D0.4 — the owner is never body-trusted).
     */
    ownerImUserId: z.string().optional(),
    ...personEditableFields,
});
export type CreateContactRequest = z.infer<typeof createContactRequestSchema>;

// Update shape: every clearable field is `.nullable().optional()` — a clear
// travels as an explicit null (aligned with the update-contact Action input),
// never as '' (which would violate e.g. the date-of-birth date shape).
export const updateContactRequestSchema = z.object({
    firstName: z.string().nullable().optional(),
    middleNames: z.string().nullable().optional(),
    lastName: z.string().nullable().optional(),
    title: z.string().nullable().optional(),
    dateOfBirth: z.string().nullable().optional(),
    gender: z.string().nullable().optional(),
    maritalStatus: z.string().nullable().optional(),
    nationality: z.string().nullable().optional(),
    countryOfResidence: z.string().nullable().optional(),
    addressLine1: z.string().nullable().optional(),
    addressLine2: z.string().nullable().optional(),
    city: z.string().nullable().optional(),
    county: z.string().nullable().optional(),
    postcode: z.string().nullable().optional(),
    country: z.string().nullable().optional(),
    email: z.string().nullable().optional(),
    phone: z.string().nullable().optional(),
    secondaryEmail: z.string().nullable().optional(),
    homePhone: z.string().nullable().optional(),
    workNumber: z.string().nullable().optional(),
    employer: z.string().nullable().optional(),
    occupation: z.string().nullable().optional(),
    preferredMethodOfContact: z.string().nullable().optional(),
    vulnerable: z.boolean().nullable().optional(),
    vulnerableNotes: z.string().nullable().optional(),
    inceptionDate: z.string().nullable().optional(),
});
export type UpdateContactRequest = z.infer<typeof updateContactRequestSchema>;

export const reassignContactOwnerRequestSchema = z.object({
    ownerImUserId: z.string().min(1),
});
export type ReassignContactOwnerRequest = z.infer<typeof reassignContactOwnerRequestSchema>;

export const setContactStatusRequestSchema = z.object({
    action: contactLifecycleActionSchema,
});
export type SetContactStatusRequest = z.infer<typeof setContactStatusRequestSchema>;

export const createOrganisationContactRequestSchema = z.object({
    organisationName: z.string().min(1),
    organisationType: organisationTypeSchema,
    organisationTypeOther: z.string().optional(),
    namedIndividual: z.string().optional(),
    inceptionDate: z.string().optional(),
    registeredAddressLine1: z.string().optional(),
    registeredAddressLine2: z.string().optional(),
    registeredCity: z.string().optional(),
    registeredCounty: z.string().optional(),
    registeredPostcode: z.string().optional(),
    registeredCountry: z.string().optional(),
    correspondenceAddressLine1: z.string().optional(),
    correspondenceAddressLine2: z.string().optional(),
    correspondenceCity: z.string().optional(),
    correspondenceCounty: z.string().optional(),
    correspondencePostcode: z.string().optional(),
    correspondenceCountry: z.string().optional(),
});
export type CreateOrganisationContactRequest = z.infer<
    typeof createOrganisationContactRequestSchema
>;

// Update shape: the name and type stay non-nullable (an organisation cannot
// lose them); every other clearable field travels its clear as explicit null,
// aligned with the update-organisation-contact Action input.
export const updateOrganisationContactRequestSchema = z.object({
    organisationName: z.string().optional(),
    organisationType: organisationTypeSchema.optional(),
    organisationTypeOther: z.string().nullable().optional(),
    namedIndividual: z.string().nullable().optional(),
    inceptionDate: z.string().nullable().optional(),
    registeredAddressLine1: z.string().nullable().optional(),
    registeredAddressLine2: z.string().nullable().optional(),
    registeredCity: z.string().nullable().optional(),
    registeredCounty: z.string().nullable().optional(),
    registeredPostcode: z.string().nullable().optional(),
    registeredCountry: z.string().nullable().optional(),
    correspondenceAddressLine1: z.string().nullable().optional(),
    correspondenceAddressLine2: z.string().nullable().optional(),
    correspondenceCity: z.string().nullable().optional(),
    correspondenceCounty: z.string().nullable().optional(),
    correspondencePostcode: z.string().nullable().optional(),
    correspondenceCountry: z.string().nullable().optional(),
});
export type UpdateOrganisationContactRequest = z.infer<
    typeof updateOrganisationContactRequestSchema
>;

const okContact = z.object({ ok: z.boolean(), contactId: z.string() });

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

export const contactsContract = c.router(
    {
        listContacts: {
            method: 'GET',
            path: '/contacts',
            query: z
                .object({
                    search: z.string().optional(),
                    contactKind: contactKindSchema.optional(),
                    status: contactStatusSchema.optional(),
                })
                .optional(),
            responses: { 200: listContactsResponseSchema },
            summary: 'Unified contacts directory — person and organisation rows',
        },
        duplicateCandidates: {
            method: 'GET',
            path: '/contacts/duplicate-candidates',
            query: z.object({
                contactKind: contactKindSchema,
                firstName: z.string().optional(),
                lastName: z.string().optional(),
                dateOfBirth: z.string().optional(),
                organisationName: z.string().optional(),
            }),
            responses: { 200: duplicateCandidatesResponseSchema },
            summary: 'Advisory duplicate candidates for a proposed contact (never blocking)',
        },
        getContact: {
            method: 'GET',
            path: '/contacts/:contactId',
            responses: { 200: contactDetailSchema, 404: errorBody },
            summary: 'Contact detail — person or organisation, discriminated on contactKind',
        },
        createContact: {
            method: 'POST',
            path: '/contacts',
            body: createContactRequestSchema,
            responses: { 200: okContact, 400: errorBody },
            summary: 'Create a person Contact in draft (create-contact Action)',
        },
        updateContact: {
            method: 'PATCH',
            path: '/contacts/:contactId',
            body: updateContactRequestSchema,
            responses: { 200: okContact, 400: errorBody, 404: errorBody },
            summary: 'Edit a person Contact (update-contact Action)',
        },
        setContactStatus: {
            method: 'POST',
            path: '/contacts/:contactId/status',
            body: setContactStatusRequestSchema,
            responses: { 200: okContact, 400: errorBody, 404: errorBody },
            summary: 'Lifecycle a person Contact (activate / close / reopen Actions)',
        },
        reassignContactOwner: {
            method: 'POST',
            path: '/contacts/:contactId/owner',
            body: reassignContactOwnerRequestSchema,
            responses: { 200: okContact, 400: errorBody, 404: errorBody },
            summary:
                "Move a Contact to a different IM's book (ops-mediated, reassign-contact-owner Action)",
        },
        createOrganisationContact: {
            method: 'POST',
            path: '/organisation-contacts',
            body: createOrganisationContactRequestSchema,
            responses: { 200: okContact, 400: errorBody },
            summary: 'Create an organisation contact in draft (create-organisation-contact Action)',
        },
        updateOrganisationContact: {
            method: 'PATCH',
            path: '/organisation-contacts/:contactId',
            body: updateOrganisationContactRequestSchema,
            responses: { 200: okContact, 400: errorBody, 404: errorBody },
            summary: 'Edit an organisation contact (update-organisation-contact Action)',
        },
        setOrganisationContactStatus: {
            method: 'POST',
            path: '/organisation-contacts/:contactId/status',
            body: setContactStatusRequestSchema,
            responses: { 200: okContact, 400: errorBody, 404: errorBody },
            summary: 'Lifecycle an organisation contact (activate / close / reopen Actions)',
        },
    },
    {
        pathPrefix: '/api',
        strictStatusCodes: false,
    },
);
